import {
  ClientInvoiceDraftItem,
  ClientOrderItem,
  findSectionIriByEntityIri,
  RouteEntitySubscriberService,
  SignalTopicBinding,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
} from 'shared';
import { SupplierInvoiceCoordinator } from '../SupplierInvoiceCoordinator/supplier-invoice.coordinator';

const supplierInvoiceRefreshPlanBuilder = (args: {
  tracker: RouteEntitySubscriberService;
  coordinator: SupplierInvoiceCoordinator;
}): SignalTopicBinding => {
  const { tracker, coordinator } = args;

  const itemsBySectionSig = tracker.getSectionItemsSignal<SupplierInvoiceItem>('supplierInvoice');
  const mappingsBySectionSig = tracker.getItemMappingsSignal<SupplierInvoiceItemMapping>('supplierInvoice');
  const linkedBySectionSig = tracker.getLinkedItemsSignal<ClientOrderItem>('supplierInvoice');
  const draftsBySectionSig = tracker.getDraftItemsSignal<ClientInvoiceDraftItem>('supplierInvoice');

  // --- resolvers ---
  const sectionByItem = (itemIri: string): string | undefined => {
    return findSectionIriByEntityIri(itemsBySectionSig(), itemIri) ?? undefined;
  };

  const sectionByMapping = (mappingIri: string): string | undefined => {
    const bySection = mappingsBySectionSig() ?? {};
    for (const [secIri, arr] of Object.entries(bySection)) {
      if (((arr as SupplierInvoiceItemMapping[]) ?? []).some((m) => m['@id'] === mappingIri)) return secIri;
    }
    return undefined;
  };

  const sectionByLinked = (linkedIri: string): string | undefined => {
    const bySection = linkedBySectionSig() ?? {};
    for (const [secIri, arr] of Object.entries(bySection)) {
      if (((arr as ClientOrderItem[]) ?? []).some((it) => it['@id'] === linkedIri)) return secIri;
    }
    return undefined;
  };

  const sectionByDraft = (draftIri: string): string | undefined => {
    const bySection = draftsBySectionSig() ?? {};
    for (const [secIri, arr] of Object.entries(bySection)) {
      if (((arr as ClientInvoiceDraftItem[]) ?? []).some((d) => d['@id'] === draftIri)) return secIri;
    }
    return undefined;
  };

  const draftItemsBySection = (topic: string): string => {
    // "/supplier_invoice_sections/123/draft_items" -> "/supplier_invoice_sections/123"
    return topic.replace(/\/draft_items$/, '');
  };

  return {
    patterns: {
      entity: /\/supplier_invoices\/\d+$/,
      totals: /\/supplier_invoices\/\d+\/totals$/,
      sections: /\/supplier_invoice_sections\/?$/,
      sectionItems: /\/supplier_invoice_sections\/\d+$/,
      items: /\/supplier_invoice_items\/\d+$/,
      itemMappings: /\/supplier_invoice_item_mappings\/\d+$/,
      linkedItems: /\/client_order_items\/\d+$/,
      draftItems: /\/client_invoice_draft_items\/\d+$/,
      draftItemsBySection: /\/?supplier_invoice_sections\/\d+\/draft_items$/,
    },
    resolvers: {
      sectionByItem,
      sectionByMapping,
      sectionByLinked,
      sectionByDraft,
      draftItemsBySection,
    },
    refresh: {
      entity: () => coordinator.refreshInvoiceEntity(),
      totals: () => coordinator.refreshTotals(),
      sections: () => coordinator.refreshSectionsList(),
      sectionItems: (sectionIri: string) => coordinator.refreshSection(sectionIri),
      items: (_sectionIri: string | undefined, itemIri: string) => coordinator.refreshSectionItem(itemIri),
      itemMappings: (sectionIri: string, itemIri?: string) =>
        itemIri
          ? coordinator.refreshItemMappingForLine(sectionIri, itemIri)
          : coordinator.refreshSectionMappings(sectionIri),
      linkedItems: (sectionIri: string) => coordinator.refreshSectionLinkedItems(sectionIri),
      draftItems: (sectionIri: string) => coordinator.refreshSectionDraftItems(sectionIri),
      draftItemsBySection: (sectionIri: string) => coordinator.forceReloadDraftItems(sectionIri),
    },
  };
};

export { supplierInvoiceRefreshPlanBuilder };
